import React, { useEffect, useState } from "react";
import { MapPin, Phone, Clock, ExternalLink, Globe } from "lucide-react";
import Reveal from "./Reveal";
import { ModelStrip } from "./Models";

const mapsUrl = (address) =>
  `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;

const prettyHost = (url) => url.replace(/^https?:\/\//, "").replace(/\/$/, "");

export const VenueCard = ({ venue, delay = 0 }) => {
  const shots = (venue.gallery || []).slice(0, 6);
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  const hero = shots[active] || { img: venue.lead, caption: venue.name };
  const t = venue.theme;

  useEffect(() => {
    if (paused || shots.length < 2) return;
    const id = setInterval(() => setActive((i) => (i + 1) % shots.length), 3800);
    return () => clearInterval(id);
  }, [paused, shots.length]);

  return (
    <Reveal delay={delay}>
      <article
        data-testid={`venue-card-${venue.slug}`}
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
        className={`grid overflow-hidden rounded-2xl border lg:grid-cols-[1.15fr_1fr] ${t.rule} ${t.bg}`}
      >
        {/* left: auto-rotating gallery */}
        <div className="flex flex-col">
          <div className="relative aspect-[16/10] w-full overflow-hidden bg-black/5">
            <img
              key={hero.img}
              src={hero.img}
              alt={hero.caption}
              loading="lazy"
              className="h-full w-full object-cover"
              data-testid={`venue-card-hero-${venue.slug}`}
            />
            <span
              className={`absolute left-4 top-4 rounded-full px-3 py-1 text-xs font-semibold ${t.chip}`}
            >
              {venue.badge}
            </span>
            <p className="absolute inset-x-0 bottom-0 bg-black/55 px-4 py-2.5 text-xs text-white">
              {hero.caption}
            </p>
          </div>

          {shots.length > 1 && (
            <div className="flex gap-2 px-5 pt-4">
              {shots.map((s, i) => (
                <button
                  key={s.img}
                  data-testid={`venue-card-thumb-${venue.slug}-${i}`}
                  onClick={() => setActive(i)}
                  aria-label={s.caption}
                  className={`h-14 flex-1 overflow-hidden rounded-md border-2 transition-all ${
                    active === i
                      ? "border-current opacity-100"
                      : "border-transparent opacity-55 hover:opacity-100"
                  }`}
                >
                  <img
                    src={s.img}
                    alt={s.caption}
                    loading="lazy"
                    className="h-full w-full object-cover"
                  />
                </button>
              ))}
            </div>
          )}

          <div className="px-5 pb-6">
            <ModelStrip venue={venue} perRow={6} rows={3} />
          </div>
        </div>

        {/* right: details + map */}
        <div className="flex flex-col border-t border-black/10 p-6 sm:p-8 lg:border-l lg:border-t-0">
          <h3 className="font-serif text-3xl leading-tight text-foreground sm:text-4xl">
            {venue.name}
          </h3>
          <p className={`mt-2 text-xs uppercase tracking-[0.2em] ${t.accent}`}>
            {venue.suburb} &middot; {venue.established}
          </p>
          <p className="mt-4 text-sm leading-relaxed text-muted-foreground">{venue.tagline}</p>

          <div className="mt-6 space-y-3 border-t border-black/10 pt-5 text-sm">
            <a
              href={mapsUrl(venue.contact.address)}
              target="_blank"
              rel="noreferrer"
              data-testid={`venue-card-address-${venue.slug}`}
              className="flex items-start gap-3 text-foreground underline decoration-dotted underline-offset-4 transition-colors hover:text-primary"
            >
              <MapPin className={`mt-0.5 h-4 w-4 flex-shrink-0 ${t.accent}`} />
              <span>
                {venue.contact.address}
                <ExternalLink className="ml-1.5 inline h-3 w-3 align-baseline" />
              </span>
            </a>
            {venue.contact.phone ? (
            <a
              href={`tel:${venue.contact.phone.replace(/[^0-9+]/g, "")}`}
              data-testid={`venue-card-phone-${venue.slug}`}
              className="flex items-center gap-3 font-medium text-foreground transition-colors hover:text-primary"
            >
              <Phone className={`h-4 w-4 flex-shrink-0 ${t.accent}`} />
              {venue.contact.phone}
            </a>
            ) : null}
            {venue.contact.website ? (
            <a
              href={venue.contact.website}
              target="_blank"
              rel="noreferrer"
              data-testid={`venue-card-website-${venue.slug}`}
              className="flex items-center gap-3 text-foreground underline decoration-dotted underline-offset-4 transition-colors hover:text-primary"
            >
              <Globe className={`h-4 w-4 flex-shrink-0 ${t.accent}`} />
              <span>
                {prettyHost(venue.contact.website)}
                <ExternalLink className="ml-1.5 inline h-3 w-3 align-baseline" />
              </span>
            </a>
            ) : null}
            {venue.contact.hours ? (
            <p className="flex items-center gap-3 text-muted-foreground">
              <Clock className={`h-4 w-4 flex-shrink-0 ${t.accent}`} />
              {venue.contact.hours}
            </p>
            ) : null}
          </div>

          <a
            href={mapsUrl(venue.contact.address)}
            target="_blank"
            rel="noreferrer"
            data-testid={`venue-card-map-${venue.slug}`}
            className="group mt-5 flex-1 overflow-hidden rounded-xl border border-black/10"
          >
            <img
              src={`/media/maps/${venue.slug}.jpg`}
              alt={`Map of ${venue.name}, ${venue.contact.address}`}
              loading="lazy"
              className="h-full min-h-[260px] w-full object-cover transition-transform duration-700 group-hover:scale-105"
            />
          </a>
        </div>
      </article>
    </Reveal>
  );
};

export default VenueCard;
